export type EnvironmentKind =
  | 'production'
  | 'staging'
  | 'local'
  | 'development'
  | 'test'
  | 'unknown';

export type DeploymentEnvironment = 'dev' | 'stage' | 'prod';

export type ParsedOrigin = {
  raw: string;
  hostname: string;
  protocol: string;
  isLocalhost: boolean;
};

export type DetectEnvironmentOptions = {
  origin?: string;
  mode?: string;
  productionHostIndicators?: string[];
  stagingIndicators?: string[];
  localHostIndicators?: string[];
};

const DEFAULT_PRODUCTION_HOST_INDICATORS = ['.version2.io', '.filehelix.com'];
const DEFAULT_STAGING_INDICATORS = [
  'stage',
  'staging',
  'release-stage',
  'pages.dev',
];
const DEFAULT_LOCAL_HOST_INDICATORS = [
  'localhost',
  '.local',
  '127.0.0.1',
  '0.0.0.0',
];

function normalize(value: string | undefined): string {
  return (value || '').toLowerCase().trim();
}

function includesAny(target: string, indicators: string[]): boolean {
  return indicators.some((indicator) => target.includes(normalize(indicator)));
}

export function parseOrigin(origin?: string): ParsedOrigin {
  const fallback = {
    raw: normalize(origin),
    hostname: '',
    protocol: '',
    isLocalhost: false,
  };

  if (!origin) {
    return fallback;
  }

  try {
    const parsed = new URL(origin);
    const hostname = normalize(parsed.hostname);
    const protocol = normalize(parsed.protocol);

    return {
      raw: normalize(origin),
      hostname,
      protocol,
      isLocalhost: includesAny(hostname, DEFAULT_LOCAL_HOST_INDICATORS),
    };
  } catch {
    const raw = normalize(origin);
    return {
      raw,
      hostname: raw,
      protocol: '',
      isLocalhost: includesAny(raw, DEFAULT_LOCAL_HOST_INDICATORS),
    };
  }
}

export function detectEnvironment(
  options: DetectEnvironmentOptions = {},
): EnvironmentKind {
  const originData = parseOrigin(options.origin);
  const mode = normalize(options.mode);

  const productionIndicators =
    options.productionHostIndicators || DEFAULT_PRODUCTION_HOST_INDICATORS;
  const stagingIndicators =
    options.stagingIndicators || DEFAULT_STAGING_INDICATORS;
  const localIndicators =
    options.localHostIndicators || DEFAULT_LOCAL_HOST_INDICATORS;

  const originText = `${originData.raw} ${originData.hostname}`.trim();

  const modeIsTest = mode === 'test';
  const modeIsLocal = mode === 'local';
  const modeIsDev = mode === 'development' || mode === 'dev';
  const modeIsStaging = mode === 'stage' || mode === 'staging';
  const modeIsProduction = mode === 'production' || mode === 'prod';

  const originIsLocal = includesAny(originText, localIndicators);
  const originIsStaging = includesAny(originText, stagingIndicators);
  const originIsProduction = includesAny(originText, productionIndicators);

  if (modeIsTest) {
    return 'test';
  }

  if (modeIsStaging || originIsStaging) {
    return 'staging';
  }

  if (modeIsLocal || originIsLocal) {
    return 'local';
  }

  if (modeIsDev) {
    return 'development';
  }

  if (modeIsProduction || originIsProduction) {
    return 'production';
  }

  return 'unknown';
}

export function toDeploymentEnvironment(
  environment: EnvironmentKind | string | undefined,
): DeploymentEnvironment {
  const normalized = normalize(environment);

  if (normalized === 'prod' || normalized === 'production') {
    return 'prod';
  }

  if (normalized === 'stage' || normalized === 'staging') {
    return 'stage';
  }

  return 'dev';
}

export function isLocalEnvironment(
  options: DetectEnvironmentOptions = {},
): boolean {
  const env = detectEnvironment(options);
  return env === 'local' || env === 'development' || env === 'test';
}

export function isStagingEnvironment(
  options: DetectEnvironmentOptions = {},
): boolean {
  return detectEnvironment(options) === 'staging';
}

export function isProductionEnvironment(
  options: DetectEnvironmentOptions = {},
): boolean {
  return detectEnvironment(options) === 'production';
}
